import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, retry, shareReplay, tap, throwError, timer } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { SKIP_AUTH_REFRESH } from '../../interceptors/auth-expired.interceptor';

export interface OrganizationSummary {
  id?: string;
  name?: string;
}

export interface OrganizationPage<T = OrganizationSummary> {
  content: T[];
  totalElements: number;
  number: number;
  size: number;
}

export interface CreateOrganizationRequest {
  name: string;
  orgType?: 'LANDLORD_SELF' | 'PROPERTY_MANAGER' | null;
  portalSlug?: string | null;
  address: string;
  state: string;
  city: string;
  country: string;
  zipCode: string;
  contactPersonFirstName: string;
  contactPersonLastName: string;
  contactEmail: string;
  contactPhone: string;
  website?: string | null;
  timeZone: string;
  currency: string;
  locale: string;
}

export interface OrganizationResponse {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  contactEmail: string;
  contactPhone: string;
  contactPersonFirstName: string;
  contactPersonLastName: string;
  website?: string | null;
  timeZone: string;
  currency: string;
  locale: string;
  status: string;
  portalSlug: string;
  orgType: string;
  domainStatus: string;
  createdDate: string;
  createdBy: string;
  lastModifiedDate: string;
  modifiedBy: string;
  version: number;
}

export interface WorkspaceSlugAvailabilityResponse {
  portalSlug: string;
  available: boolean;
  message?: string | null;
}

export type OrganizationContextItem = {
  orgId: string;
  orgName: string;
  portalSlug?: string | null;
  orgType?: string | null;
  orgRole: string;
  portalMode?: 'PROPERTY_MANAGER' | 'LANDLORD_SELF' | 'OWNER_PORTAL' | 'TENANT' | string | null;
  landingPath?: string | null;
  owner: boolean;
  staff: boolean;
  subscriptionActive: boolean;
  permissions: string[];
  authorizationPolicyVersion?: string | null;
};

export type OrganizationContextResponse = {
  hasMembership: boolean;
  defaultOrgId?: string | null;
  defaultOrgName?: string | null;
  defaultOrgRole?: string | null;
  defaultOrgSubscriptionActive: boolean;
  organizations: OrganizationContextItem[];
};

export type OwnerInvitation = {
  id: string;
  organizationId: string;
  organizationName: string;
  portalSlug?: string | null;
  ownerClientId: string;
  ownerName: string;
  email: string;
  status: string;
  membershipStatus: string;
  existingUser: boolean;
  expiresAt?: string | null;
  acceptedAt?: string | null;
  rejectedAt?: string | null;
  message?: string | null;
  redirectUrl?: string | null;
};

export type StaffInvitation = {
  id: string;
  organizationId: string;
  organizationName: string;
  portalSlug?: string | null;
  email: string;
  role?: string | null;
  accessScopeType?: string | null;
  status: string;
  invitedAt?: string | null;
  expiresAt?: string | null;
  acceptedAt?: string | null;
  declinedAt?: string | null;
  message?: string | null;
  redirectUrl?: string | null;
};

export type AddressSuggestion = {
  label: string;
  line1?: string | null;
  city?: string | null;
  municipality?: string | null;
  formattedAddress?: string | null;
  state?: string | null;
  stateCode?: string | null;
  postalCode?: string | null;
  country?: string | null;
  countryCode?: string | null;
  provider?: string | null;
  placeId?: string | null;
  confidence?: number | null;
};

export type NormalizedAddress = {
  formattedAddress?: string | null;
  line1?: string | null;
  city?: string | null;
  municipality?: string | null;
  county?: string | null;
  state?: string | null;
  stateCode?: string | null;
  postalCode?: string | null;
  country?: string | null;
  countryCode?: string | null;
  street?: string | null;
  houseNumber?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  provider?: string | null;
  placeId?: string | null;
  confidence?: number | null;
  addressType?: string | null;
};

const SUPPORTED_ADDRESS_COUNTRIES = 'us,ca';

@Injectable({
  providedIn: 'root',
})
export class OrganizationOnboardingService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiGatewayUrl}/property-service`;
  private context$: Observable<OrganizationContextResponse> | null = null;

  getOrganizationContext(options: { skipAuthRefresh?: boolean } = {}): Observable<OrganizationContextResponse> {
    if (!this.context$) {
      this.context$ = this.http.get<OrganizationContextResponse>(`${this.base}/organization/context`, {
        context: options.skipAuthRefresh ? new HttpContext().set(SKIP_AUTH_REFRESH, true) : undefined,
        withCredentials: true,
      }).pipe(
        retry({
          count: 2,
          delay: (_error, retryCount) => timer(retryCount * 300),
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
        catchError((error) => {
          this.context$ = null;
          return throwError(() => error);
        }),
      );
    }

    return this.context$;
  }

  getMyOwnerInvitations(): Observable<OwnerInvitation[]> {
    return this.http.get<OwnerInvitation[]>(`${this.base}/api/v1/me/owner-invitations`, {
      withCredentials: true,
    });
  }

  acceptMyOwnerInvitation(invitationId: string): Observable<OwnerInvitation> {
    return this.http.post<OwnerInvitation>(
      `${this.base}/api/v1/me/owner-invitations/${invitationId}/accept`,
      {},
      { withCredentials: true },
    );
  }

  rejectMyOwnerInvitation(invitationId: string): Observable<OwnerInvitation> {
    return this.http.post<OwnerInvitation>(
      `${this.base}/api/v1/me/owner-invitations/${invitationId}/reject`,
      {},
      { withCredentials: true },
    );
  }

  getMyStaffInvitations(): Observable<StaffInvitation[]> {
    return this.http.get<StaffInvitation[]>(`${this.base}/api/v1/me/staff-invitations`, {
      withCredentials: true,
    });
  }

  acceptMyStaffInvitation(invitationId: string): Observable<StaffInvitation> {
    return this.http.post<StaffInvitation>(
      `${this.base}/api/v1/me/staff-invitations/${invitationId}/accept`,
      {},
      { withCredentials: true },
    );
  }

  rejectMyStaffInvitation(invitationId: string): Observable<StaffInvitation> {
    return this.http.post<StaffInvitation>(
      `${this.base}/api/v1/me/staff-invitations/${invitationId}/reject`,
      {},
      { withCredentials: true },
    );
  }

  refreshOrganizationContext(options: { skipAuthRefresh?: boolean } = {}): Observable<OrganizationContextResponse> {
    this.clearOrganizationContextCache();
    return this.getOrganizationContext(options);
  }

  clearOrganizationContextCache(): void {
    this.context$ = null;
  }

  getOrganizations(page = 0, size = 10, includeStaff = true): Observable<OrganizationPage> {
    const params = new HttpParams()
      .set('page', page)
      .set('size', size)
      .set('includeStaff', includeStaff);

    return this.http.get<OrganizationPage>(`${this.base}/organization`, {
      params,
      withCredentials: true,
    });
  }

  autocompleteAddress(
    text: string,
    bias?: { lat: number; lon: number } | null,
    orgId?: string | null,
    countries = SUPPORTED_ADDRESS_COUNTRIES,
  ): Observable<AddressSuggestion[]> {
    let params = new HttpParams().set('text', text);

    if (countries) {
      params = params.set('countries', countries);
    }

    if (bias) {
      params = params.set('biasLat', bias.lat).set('biasLon', bias.lon);
    }

    const url = orgId
      ? `${this.base}/organizations/${orgId}/addresses/autocomplete`
      : `${this.base}/address-lookup/autocomplete`;

    return this.http.get<AddressSuggestion[]>(url, {
      params,
      withCredentials: true,
    }).pipe(map((suggestions) => suggestions.filter((suggestion) => this.isSupportedAddressCountry(suggestion))));
  }

  resolveAddress(
    query: string,
    placeId?: string | null,
    bias?: { lat: number; lon: number } | null,
    orgId?: string | null,
    countries = SUPPORTED_ADDRESS_COUNTRIES,
  ): Observable<NormalizedAddress> {
    let params = new HttpParams().set('query', query);

    if (countries) {
      params = params.set('countries', countries);
    }

    if (placeId) {
      params = params.set('placeId', placeId);
    }

    if (bias) {
      params = params.set('biasLat', bias.lat).set('biasLon', bias.lon);
    }

    const url = orgId
      ? `${this.base}/organizations/${orgId}/addresses/resolve`
      : `${this.base}/address-lookup/resolve`;

    return this.http.get<NormalizedAddress>(url, {
      params,
      withCredentials: true,
    });
  }

  private isSupportedAddressCountry(address: AddressSuggestion | NormalizedAddress): boolean {
    const country = String(address.countryCode || address.country || '').trim().toLowerCase();

    if (!country) {
      return true;
    }

    return ['us', 'usa', 'united states', 'united states of america', 'ca', 'can', 'canada'].includes(country);
  }

  checkWorkspaceSlugAvailability(slug: string): Observable<WorkspaceSlugAvailabilityResponse> {
    return this.http.get<WorkspaceSlugAvailabilityResponse>(
      `${this.base}/api/v1/public/workspaces/${encodeURIComponent(slug)}/availability`,
    );
  }

  createOrganization(
    organization: CreateOrganizationRequest,
    logo?: File | null,
    cover?: File | null,
    options: { skipAuthRefresh?: boolean } = {},
  ): Observable<OrganizationResponse> {
    const formData = new FormData();

    const organizationBlob = new Blob([JSON.stringify(organization)], {
      type: 'application/json',
    });

    formData.append('organization', organizationBlob);

    if (logo) {
      formData.append('logo', logo, logo.name);
    }

    if (cover) {
      formData.append('cover', cover, cover.name);
    }

    return this.http.post<OrganizationResponse>(`${this.base}/organization`, formData, {
      context: options.skipAuthRefresh ? new HttpContext().set(SKIP_AUTH_REFRESH, true) : undefined,
      withCredentials: true,
    }).pipe(tap(() => this.clearOrganizationContextCache()));
  }
}
